import { HowDeterminedButton } from "@components/provenance/HowDeterminedButton";
import { Button } from "@components/shared/Button";
import { Card } from "@components/shared/Card";
import { StateBadge, type StateBadgeTone } from "@components/shared/StateBadge";
import { REGENERATE_CAMPAIGN_DECISION_SUMMARY } from "@lib/graphql/mutations";
import { selectActiveTasks, useBackgroundTasksStore } from "@lib/stores/backgroundTasksStore";
import { AlertTriangle, ArrowRight, RefreshCw, Sparkles, Users } from "lucide-react";
import { Link } from "react-router";
import { useMutation } from "urql";
import type { CampaignDecisionSummary } from "./context";

interface Props {
  campaignId: string;
  summary: CampaignDecisionSummary | null | undefined;
  candidateCount: number;
  scoredCandidateCount: number;
}

const STRENGTH_TONE: Record<CampaignDecisionSummary["strengthSignal"], StateBadgeTone> = {
  high: "success",
  mixed: "warning",
  low: "error",
  unknown: "muted",
};

const STRENGTH_LABEL: Record<CampaignDecisionSummary["strengthSignal"], string> = {
  high: "Strong pool",
  mixed: "Mixed pool",
  low: "Weak pool",
  unknown: "Not enough data",
};

function formatGeneratedAt(iso: string): string {
  const then = new Date(iso);
  const now = new Date();
  const minutes = Math.floor((now.getTime() - then.getTime()) / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hr ago`;
  return then.toLocaleDateString();
}

function EmptyHeader() {
  return (
    <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-text-muted">
      <Sparkles size={12} /> Decision Summary
    </div>
  );
}

interface EmptyStateProps {
  campaignId: string;
  candidateCount: number;
  scoredCandidateCount: number;
  inFlight: boolean;
  onGenerate: () => void;
}

function EmptyState({
  campaignId,
  candidateCount,
  scoredCandidateCount,
  inFlight,
  onGenerate,
}: EmptyStateProps) {
  // State A: no candidates yet
  if (candidateCount === 0) {
    return (
      <Card tone="decision" className="mb-6" data-testid="decision-summary-empty-no-candidates">
        <EmptyHeader />
        <p className="text-sm text-text-secondary">
          Once you've added candidates, Rarion will summarise the pool here — at a glance, how
          strong it is, where the main gap lies, and what to do next.
        </p>
        <div className="mt-3">
          <Link
            to={`/campaigns/${campaignId}/pool`}
            className="inline-flex items-center gap-1 text-sm font-medium text-link hover:text-link-hover"
          >
            <Users size={14} /> Add candidates to the longlist
            <ArrowRight size={14} />
          </Link>
        </div>
      </Card>
    );
  }

  // State B: candidates added, none assessed yet
  if (scoredCandidateCount === 0) {
    return (
      <Card tone="decision" className="mb-6" data-testid="decision-summary-empty-no-scores">
        <EmptyHeader />
        <p className="text-sm text-text-secondary">
          Score at least one candidate and Rarion will summarise where the pool is strong, where it
          falls short, and which candidate group to prioritise next.
        </p>
        <div className="mt-3">
          <Link
            to={`/campaigns/${campaignId}/run/pipeline/scoring`}
            className="inline-flex items-center gap-1 text-sm font-medium text-link hover:text-link-hover"
          >
            Go to the Recruiter Assessment stage
            <ArrowRight size={14} />
          </Link>
        </div>
      </Card>
    );
  }

  // State C: ready — let them build it
  return (
    <Card tone="decision" className="mb-6" data-testid="decision-summary-empty-ready">
      <div className="flex items-start justify-between gap-3">
        <div>
          <EmptyHeader />
          <p className="text-sm text-text-secondary">
            You've scored {scoredCandidateCount}{" "}
            {scoredCandidateCount === 1 ? "candidate" : "candidates"}. Build the summary to see pool
            strength, the main gap, and the recommended next action.
          </p>
        </div>
        <Button onClick={onGenerate} disabled={inFlight} data-testid="decision-summary-generate">
          {inFlight ? (
            <>
              <RefreshCw size={14} className="animate-spin" /> Building…
            </>
          ) : (
            <>
              <Sparkles size={14} /> Build summary
            </>
          )}
        </Button>
      </div>
    </Card>
  );
}

export function DecisionSummaryPanel({
  campaignId,
  summary,
  candidateCount,
  scoredCandidateCount,
}: Props) {
  const [, regenerate] = useMutation(REGENERATE_CAMPAIGN_DECISION_SUMMARY);
  const inFlight = useBackgroundTasksStore((s) =>
    selectActiveTasks(s.tasksById).some(
      (t) =>
        t.kind === "campaign_decision_summary" &&
        t.subjectType === "Campaign" &&
        t.subjectId === campaignId,
    ),
  );

  const handleRegenerate = () => {
    regenerate({ input: { campaignId } });
  };

  if (!summary) {
    return (
      <EmptyState
        campaignId={campaignId}
        candidateCount={candidateCount}
        scoredCandidateCount={scoredCandidateCount}
        inFlight={inFlight}
        onGenerate={handleRegenerate}
      />
    );
  }

  return (
    <Card tone="decision" className="mb-6" data-testid="decision-summary-panel">
      <div className="flex items-start justify-between gap-3">
        <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
          <span className="inline-flex items-center gap-1">
            <Sparkles size={12} /> Decision Summary
          </span>
        </div>
        <div className="flex items-center gap-3">
          {summary.stale && (
            <span data-testid="decision-summary-stale">
              <StateBadge
                tone="warning"
                label="Out of date"
                title="Candidates, steps or outcomes have changed since this summary was built — refresh to bring it up to date"
              />
            </span>
          )}
          <HowDeterminedButton
            subject={{ kind: "decision_summary", campaignId }}
            className="text-[10px] text-text-faint underline decoration-dotted underline-offset-2 hover:text-text-secondary"
          />
          <span className="text-xs text-text-faint">
            as of {formatGeneratedAt(summary.generatedAt)}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleRegenerate}
            disabled={inFlight}
            data-testid="decision-summary-regenerate"
          >
            <RefreshCw size={14} className={inFlight ? "animate-spin" : ""} />
            {inFlight ? "Refreshing…" : "Refresh"}
          </Button>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-3">
        <div>
          <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-text-muted">
            Candidate strength
          </div>
          <StateBadge
            tone={STRENGTH_TONE[summary.strengthSignal]}
            label={STRENGTH_LABEL[summary.strengthSignal]}
          />
        </div>
        <div>
          <div className="mb-1 flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-text-muted">
            <AlertTriangle size={11} /> Key gap
          </div>
          <p className="text-sm text-text-primary">{summary.keyGap}</p>
        </div>
        <div>
          <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-text-muted">
            Recommended next action
          </div>
          <p className="text-sm font-medium text-text-primary">{summary.recommendedAction}</p>
        </div>
      </div>
    </Card>
  );
}
